import React, { useEffect, useState } from "react";
import TitleStripe from "../../common/TitleStripe";
import {
  calculateRiskScore,
  formatDocument,
  formatToReal,
  getLetterScore,
  getScoreLegends,
  maskCEP,
} from "../../../utils/utils";
import { EvaluationScorePJ } from "../../common/query/EvaluationScorePJ";
import PendenciasFinanceiras from "../../common/query/PendenciasFinanceiras";
import NoOcurrence from "../../common/query/NoOcurrence";
import Protestos from "../../common/query/Protestos";
import ChequesSemFundos from "../../common/query/ChequesSemFundos";
import ChequesSustados from "../../common/query/ChequesSustados";
import AvisoCard from "../../common/query/AvisoCard";
import HistoricoConsultas from "../../common/query/HistoricoConsultas";
import ScoreRating from "./ScoreRating";
import { Badge, Button, Modal, Table } from "flowbite-react";
import { HiOutlineEye, HiThumbDown, HiThumbUp } from "react-icons/hi";
import StreetView from "../../common/query/StreetView";
import ModalReceitaFederal from "../../modals/ModalReceitaFederal";
import moment from "moment";

const calculateCompanyAgeWithMoment = (dateStr) => {
  if (!dateStr) return null;

  // tenta vários formatos comuns
  const foundation = moment(
    dateStr,
    ["DD/MM/YYYY", "YYYY-MM-DD", "YYYYMMDD"],
    true
  );
  if (!foundation.isValid()) return null;

  const now = moment();

  const years = now.diff(foundation, "years");
  const afterYears = foundation.clone().add(years, "years");
  const months = now.diff(afterYears, "months");

  if (years < 0) return null;

  const anosLabel = years > 0 ? `${years} ano${years > 1 ? "s" : ""}` : null;
  const mesesLabel =
    months > 0 ? `${months} mês${months > 1 ? "es" : ""}` : null;

  if (anosLabel && mesesLabel)
    return `${anosLabel} e ${mesesLabel} de atividade`;
  if (anosLabel) return `${anosLabel} de atividade`;
  if (mesesLabel) return `${mesesLabel} de atividade`;

  return "menos de 1 mês de atividade";
};

const formatYYYYMMToYY = (yyyyMM) => {
  if (!yyyyMM) return "–";
  const [y, m] = yyyyMM.split("-");
  return `${m}/${String(y).slice(-2)}`;
};

const formatPeriodoLabel = (periodo) => {
  if (!periodo) return "–";
  if (typeof periodo === "string" && periodo.includes("-")) {
    return formatYYYYMMToYY(periodo);
  }
  return periodo;
};

const getPeriodoFromEntry = (entry) =>
  entry?.periodo ?? entry?.mes ?? entry?.mes_ano ?? entry?.periodo_consulta;

const firstNonEmptySeries = (src, keys) => {
  for (const k of keys) {
    const arr = src?.[k];
    if (Array.isArray(arr) && arr.length) return arr;
  }
  return [];
};

const DefineRiscoQuery = ({ data, dataFull }) => {
  const [riskScore, setRiskScore] = useState(0);
  const [openMap, setOpenMap] = useState(false);
  const companyAgeLabel = calculateCompanyAgeWithMoment(
    data?.dadosCadastrais?.dataFundacao
  );

  useEffect(() => {
    setRiskScore(
      calculateRiskScore(
        +data?.resumoConsulta?.pendenciasFinanceiras?.quantidadeTotal || 0,
        +data?.resumoConsulta?.protestos?.quantidadeTotal || 0,
        +data?.score?.score,
        +data?.score?.probabilidade
      )
    );
  }, [data]);

  const fullAddress = [
    data?.dadosCadastrais?.endereco,
    data?.dadosCadastrais?.bairro,
    data?.dadosCadastrais?.cidade && data?.dadosCadastrais?.uf
      ? `${data?.dadosCadastrais?.cidade} - ${data?.dadosCadastrais?.uf}`
      : data?.dadosCadastrais?.cidade || data?.dadosCadastrais?.uf,
    maskCEP(data?.dadosCadastrais?.cep),
  ]
    .filter(Boolean)
    .join(", ");

  const statusConsumidor = data?.statusConsumidor;
  const statusConsumidorContainsNao = statusConsumidor
    ? statusConsumidor.toUpperCase().includes("NÃO") ||
      statusConsumidor.toUpperCase().includes("CANCELADA")
    : false;

  const compromissosSeries = data?.comprometimentoFuturo?.compromissos ?? [];
  const creditosSeries = data?.comprometimentoFuturo?.credito ?? [];
  const periodosSeries = [
    ...new Set(
      [
        ...compromissosSeries.map((pt) => pt.periodo).filter(Boolean),
        ...creditosSeries.map((pt) => pt.periodo).filter(Boolean),
      ].filter(Boolean)
    ),
  ];
  const getCompromissoSerie = (periodo) =>
    compromissosSeries.find((item) => item.periodo === periodo);
  const getCreditoSerie = (periodo) =>
    creditosSeries.find((item) => item.periodo === periodo);
  const formatCompromissoValor = (valor) => {
    const parsed = parseFloat(valor);
    if (isNaN(parsed)) {
      return "–";
    }
    if (parsed === 0) {
      return "< 10";
    }
    return parsed * 100;
  };
  const formatCreditoValor = (valor) => {
    const parsed = parseFloat(valor);
    return !isNaN(parsed) ? formatToReal(parsed * 1000) : "–";
  };
  const creditoObtidoSeries = data?.creditoObtido?.series ?? [];
  const compromissosOperacoesSeries = data?.compromissos?.series ?? [];
  const periodosOperacoes = [
    ...new Set(
      [
        ...creditoObtidoSeries
          .map((item) => getPeriodoFromEntry(item))
          .filter(Boolean),
        ...compromissosOperacoesSeries
          .map((item) => getPeriodoFromEntry(item))
          .filter(Boolean),
      ].filter(Boolean)
    ),
  ];
  const getCreditoObtidoPorPeriodo = (periodo) =>
    creditoObtidoSeries.find((item) => getPeriodoFromEntry(item) === periodo);
  const getCompromissosPorPeriodo = (periodo) =>
    compromissosOperacoesSeries.find(
      (item) => getPeriodoFromEntry(item) === periodo
    );
  const formatCreditoObtidoValor = (valor) => {
    const parsed = parseFloat(valor);
    return !isNaN(parsed) ? formatToReal(parsed * 1000) : "–";
  };

  return (
    <div>
      <TitleStripe>Dados Cadastrais</TitleStripe>

      <div className="mt-4 grid grid-cols-2 gap-6 bg-white border border-gray-200 rounded-xl p-6 shadow-sm text-sm">
        <div className="space-y-4">
          <div>
            <div className="text-gray-500 text-xs uppercase">Razão Social</div>
            <div className="font-medium text-gray-900">
              {data?.dadosCadastrais?.razaoSocial || "Indisponível"}
            </div>
            <ModalReceitaFederal cnpj={data?.dadosCadastrais?.cnpj} />
          </div>

          <div>
            <div className="text-gray-500 text-xs uppercase">Nome Fantasia</div>
            <div className="font-medium text-gray-900">
              {data?.dadosCadastrais?.nomeFantasia || "Indisponível"}
            </div>
          </div>

          <div>
            <div className="text-gray-500 text-xs uppercase">
              Natureza Jurídica
            </div>
            <div className="font-medium text-gray-900">
              {data?.dadosCadastrais?.naturezaJuridica || "Indisponível"}
            </div>
          </div>

          <div>
            <div className="text-gray-500 text-xs uppercase">CNPJ/MF</div>
            <div className="font-medium text-gray-900">
              {data?.dadosCadastrais?.cnpj
                ? formatDocument(data?.dadosCadastrais?.cnpj)
                : "Indisponível"}
            </div>
          </div>

          <div>
            <div className="text-gray-500 text-xs uppercase">
              CNAE Principal
            </div>
            <div className="font-medium text-gray-900">
              {data?.dadosCadastrais?.ramoAtividade || "Indisponível"}
            </div>
          </div>

          <div>
            <div className="text-gray-500 text-xs uppercase">Fundação</div>
            <div className="font-medium text-gray-900 flex flex-wrap items-center gap-2">
              <span>
                {data?.dadosCadastrais?.dataFundacao || "Indisponível"}
              </span>

              {companyAgeLabel && (
                <Badge color="gray" className="text-[11px] font-medium">
                  {companyAgeLabel}
                </Badge>
              )}
            </div>
          </div>
        </div>

        <div className="space-y-4">
          <div>
            <div className="text-gray-500 text-xs uppercase">
              Situação do CNPJ
            </div>
            <div
              className={`inline-block px-2 py-1 text-xs font-semibold rounded-md text-white ${
                data?.dadosCadastrais?.situacao?.toLowerCase() === "ativo"
                  ? "bg-green-600"
                  : "bg-red-600"
              }`}
            >
              {data?.dadosCadastrais?.situacao || "Indisponível"}
            </div>
          </div>

          <div>
            <div className="text-gray-500 text-xs uppercase">Endereço</div>
            <div className="font-medium text-gray-900">
              {data?.dadosCadastrais?.endereco || "Indisponível"}
              {fullAddress && (
                <div
                  className="flex items-center text-xs gap-1 text-blue-600 cursor-pointer"
                  onClick={() => setOpenMap(true)}
                >
                  <span>- Ver fotos do local</span>
                  <HiOutlineEye className="text-lg" />
                </div>
              )}
            </div>
          </div>

          <div>
            <div className="text-gray-500 text-xs uppercase">Bairro</div>
            <div className="font-medium text-gray-900">
              {data?.dadosCadastrais?.bairro || "Indisponível"}
            </div>
          </div>

          <div>
            <div className="text-gray-500 text-xs uppercase">Cidade/UF</div>
            <div className="font-medium text-gray-900">
              {data?.dadosCadastrais?.cidade || "Indisponível"} /{" "}
              {data?.dadosCadastrais?.uf || "--"}
            </div>
          </div>

          <div>
            <div className="text-gray-500 text-xs uppercase">CEP</div>
            <div className="font-medium text-gray-900">
              {maskCEP(data?.dadosCadastrais?.cep) || "Indisponível"}
            </div>
          </div>
        </div>
      </div>

      <div className="mb-4">
        <TitleStripe>Avaliação preliminar de crédito</TitleStripe>
      </div>

      {data?.score?.score == 2 ? (
        <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg text-yellow-800 text-sm">
          <p className="font-medium">
            Não foi possível calcular o score desta empresa.
          </p>
          <p className="mt-1">
            É necessário verificar as outras informações disponíveis no
            relatório para uma análise completa.
          </p>
        </div>
      ) : (
        <EvaluationScorePJ
          data={data}
          score={data?.score?.score}
          probability={+data?.score?.probabilidade * 100}
          type="CNPJ"
          hasIncome={false}
        />
      )}

      {dataFull?.scoreRating?.statusRetorno == 1 && (
        <ScoreRating score={dataFull?.scoreRating} block={false} />
      )}

      {statusConsumidor && (
        <div className="mt-5 w-full">
          <span
            className={`w-full text-sm block px-3 py-5 rounded-lg flex items-center gap-2 ${
              statusConsumidorContainsNao
                ? "text-red-600 bg-red-50"
                : "text-emerald-700 bg-emerald-50"
            }`}
          >
            {statusConsumidorContainsNao ? (
              <HiThumbDown className="h-5 w-5" />
            ) : (
              <HiThumbUp className="h-5 w-5" />
            )}
            <span className="font-semibold">{statusConsumidor}</span>
          </span>
        </div>
      )}

      {data?.pagamentoPontual && data?.pagamentoPontual?.series?.length > 0 && (
        <div>
          <TitleStripe className="mb-4">Pagamentos Pontuais</TitleStripe>
          {data?.pagamentoPontual ? (
            <>
              <div className="mt-4 font-semibold">
                Pontuação:{" "}
                <Badge className="w-fit inline" color="gray">
                  {data?.pagamentoPontual?.pontuacao ?? "–"}
                </Badge>
              </div>

              <div className="border border-gray-300 rounded-lg mt-2">
                <Table>
                  <Table.Head>
                    {data?.pagamentoPontual?.series?.map((pt, idx) => (
                      <Table.HeadCell
                        key={idx}
                        className="text-center text-black text-xs px-1"
                        style={{ background: "#EBF5FF" }}
                      >
                        {formatYYYYMMToYY(pt.mes)}
                      </Table.HeadCell>
                    ))}
                  </Table.Head>
                  <Table.Body className="divide-y">
                    <Table.Row>
                      {data?.pagamentoPontual?.series?.map((pt, idx) => {
                        const n = parseFloat(pt?.valor);
                        return (
                          <Table.Cell
                            key={idx}
                            className="text-center text-black text-2xs px-1"
                          >
                            {!isNaN(n) ? `${n}%` : "–"}
                          </Table.Cell>
                        );
                      })}
                    </Table.Row>
                  </Table.Body>
                </Table>
              </div>

              <div className="flex justify-center items-center mt-3">
                <span className="text-green-500 font-semibold">100%</span> :
                Pagamentos Pontuais |{" "}
                <span className="text-red-500 font-semibold ml-1">0%</span> :
                Pagamentos não pontuais
              </div>
            </>
          ) : (
            <NoOcurrence />
          )}
        </div>
      )}

      {data?.pagamentoAtrasado &&
        data?.pagamentoAtrasado?.atraso_medio_dias?.length > 0 && (
          <div>
            <TitleStripe className="mb-5">Pagamentos em atrasos</TitleStripe>

            {data?.pagamentoAtrasado ? (
              <>
                <div className="w-full rounded-lg border border-gray-300 overflow-x-auto">
                  <Table>
                    <Table.Head>
                      <Table.HeadCell
                        className="text-left text-black text-2xs px-1"
                        style={{ background: "#EBF5FF" }}
                      >
                        Tipo
                      </Table.HeadCell>

                      {/* meses do primeiro bloco com série não vazia */}
                      {firstNonEmptySeries(data?.pagamentoAtrasado, [
                        "dias_6_15",
                        "dias_16_30",
                        "dias_31_60",
                        "mais_60",
                        "atraso_medio_dias",
                      ])?.map((pt, idx) => (
                        <Table.HeadCell
                          key={idx}
                          className="text-center text-black text-2xs px-1"
                          style={{ background: "#EBF5FF" }}
                        >
                          {formatYYYYMMToYY(pt.mes)}
                        </Table.HeadCell>
                      ))}
                    </Table.Head>

                    <Table.Body className="divide-y">
                      {[
                        ["Período 6 a 15 dias", "dias_6_15"],
                        ["Período 16 a 30 dias", "dias_16_30"],
                        ["Período 31 a 60 dias", "dias_31_60"],
                        ["Mais de 60 dias", "mais_60"],
                        ["Atraso médio (dias)", "atraso_medio_dias"],
                      ].map(([label, key], i) => {
                        const series = data?.pagamentoAtrasado?.[key] || [];
                        if (!Array.isArray(series) || series.length === 0)
                          return null;

                        return (
                          <Table.Row key={i}>
                            <Table.Cell className="text-left text-black text-2xs font-medium px-1">
                              {label}
                            </Table.Cell>

                            {series.map((pt, idx) => {
                              const n = parseFloat(pt?.valor);
                              const isDias = key === "atrasoMedioDias";
                              return (
                                <Table.Cell
                                  key={idx}
                                  className="text-center text-black text-2xs px-1"
                                >
                                  {!isNaN(n)
                                    ? isDias
                                      ? `${n}d`
                                      : `${n}%`
                                    : "–"}
                                </Table.Cell>
                              );
                            })}
                          </Table.Row>
                        );
                      })}
                    </Table.Body>
                  </Table>
                </div>

                <div className="flex justify-center items-center mt-3">
                  <span className="text-red-500 font-semibold">100%</span> :
                  Pagamentos em atrasos |{" "}
                  <span className="text-green-500 font-semibold ml-1">0%</span>{" "}
                  : Pagamentos em dia
                </div>
              </>
            ) : (
              <NoOcurrence />
            )}
          </div>
        )}

      {periodosOperacoes.length > 0 ? (
        <div>
          <TitleStripe className="mb-5">Compromissos</TitleStripe>

          <div className="border border-gray-300 rounded-lg mt-2 overflow-x-auto">
            <Table>
              <Table.Head>
                <Table.HeadCell
                  className="text-left text-black text-2xs px-1"
                  style={{ background: "#EBF5FF" }}
                >
                  Indicador
                </Table.HeadCell>
                {periodosOperacoes.map((periodo) => (
                  <Table.HeadCell
                    key={periodo}
                    className="text-center text-black text-xs px-1"
                    style={{ background: "#EBF5FF" }}
                  >
                    {formatPeriodoLabel(periodo)}
                  </Table.HeadCell>
                ))}
              </Table.Head>

              <Table.Body className="divide-y">
                <Table.Row>
                  <Table.Cell className="text-left text-black text-xs px-1 font-semibold">
                    Qtd. de títulos
                  </Table.Cell>
                  {periodosOperacoes.map((periodo) => (
                    <Table.Cell
                      key={`compromissos-${periodo}`}
                      className="text-center text-black text-xs px-1"
                    >
                      {formatCompromissoValor(
                        getCompromissosPorPeriodo(periodo)?.valor
                      )}
                    </Table.Cell>
                  ))}
                </Table.Row>
                <Table.Row>
                  <Table.Cell className="text-left text-black text-xs px-1 font-semibold">
                    Valor dos títulos
                  </Table.Cell>
                  {periodosOperacoes.map((periodo) => (
                    <Table.Cell
                      key={`credito-obtido-${periodo}`}
                      className="text-center text-black text-xs px-1"
                    >
                      {formatCreditoObtidoValor(
                        getCreditoObtidoPorPeriodo(periodo)?.valor
                      )}
                    </Table.Cell>
                  ))}
                </Table.Row>
              </Table.Body>
            </Table>
          </div>

          <div className="mt-3 text-sm border border-gray-300 bg-gray-50 p-2 rounded-md">
            Descrição: Número total de obrigações financeiras registradas no
            nome do consumidor ou empresa, como boletos, duplicatas, carnês ou
            contratos de crédito. Indica a quantidade de compromissos ativos e o
            total de crédito obtido.
          </div>
        </div>
      ) : (
        <NoOcurrence />
      )}

      {periodosSeries.length > 0 ? (
        <div>
          <TitleStripe className="mb-5">Comprometimento Futuro</TitleStripe>

          <div className="border border-gray-300 rounded-lg mt-2 overflow-x-auto">
            <Table>
              <Table.Head>
                <Table.HeadCell
                  className="text-left text-black text-2xs px-1"
                  style={{ background: "#EBF5FF" }}
                >
                  Indicador
                </Table.HeadCell>
                {periodosSeries.map((periodo) => (
                  <Table.HeadCell
                    key={periodo}
                    className="text-center text-black text-xs px-1"
                    style={{ background: "#EBF5FF" }}
                  >
                    {formatPeriodoLabel(periodo)}
                  </Table.HeadCell>
                ))}
              </Table.Head>

              <Table.Body className="divide-y">
                <Table.Row>
                  <Table.Cell className="text-left text-black text-xs px-1 font-semibold">
                    Qtd. de títulos
                  </Table.Cell>
                  {periodosSeries.map((periodo) => (
                    <Table.Cell
                      key={`compromisso-${periodo}`}
                      className="text-center text-black text-xs px-1"
                    >
                      {formatCompromissoValor(
                        getCompromissoSerie(periodo)?.pontuacao
                      )}
                    </Table.Cell>
                  ))}
                </Table.Row>
                <Table.Row>
                  <Table.Cell className="text-left text-black text-xs px-1 font-semibold">
                    Valor dos títulos
                  </Table.Cell>
                  {periodosSeries.map((periodo) => (
                    <Table.Cell
                      key={`credito-${periodo}`}
                      className="text-center text-black text-2xs px-1"
                    >
                      {formatCreditoValor(getCreditoSerie(periodo)?.pontuacao)}
                    </Table.Cell>
                  ))}
                </Table.Row>
              </Table.Body>
            </Table>
          </div>

          {compromissosSeries.length > 0 && (
            <div className="mt-3 text-sm border border-gray-300 bg-gray-50 p-2 rounded-md">
              Descrição: Número total de obrigações financeiras registradas no
              nome do consumidor ou empresa, como boletos, duplicatas, carnês ou
              contratos de crédito. Indica a quantidade de compromissos ativos.
            </div>
          )}
        </div>
      ) : (
        <NoOcurrence />
      )}

      <div>
        <TitleStripe className="mb-4">Negativações Registradas</TitleStripe>

        {data?.pendenciasFinanceiras?.length > 0 ? (
          <>
            <PendenciasFinanceiras
              quantidadeTotal={
                data?.resumoConsulta?.pendenciasFinanceiras?.quantidadeTotal
              }
              valorTotal={
                data?.resumoConsulta?.pendenciasFinanceiras?.valorTotal
              }
              pendenciasFinanceiras={data?.pendenciasFinanceiras}
            />
          </>
        ) : (
          <NoOcurrence />
        )}
      </div>
      <div>
        <TitleStripe className="mb-5">Protestos</TitleStripe>
        {data?.protestos?.length > 0 ? (
          <Protestos
            quantidadeTotal={data?.resumoConsulta?.protestos?.quantidadeTotal}
            valorTotal={data?.resumoConsulta?.protestos?.valorTotal}
            protestos={data?.protestos}
            temVencimento={false}
          />
        ) : (
          <NoOcurrence />
        )}
      </div>

      <div>
        <TitleStripe className="mb-5">Cheques sem fundo</TitleStripe>
        {data?.chequesSemFundo?.length > 0 ? (
          <>
            <ChequesSemFundos
              quantidadeTotal={
                data?.resumoConsulta?.chequesSemFundo?.quantidadeTotal
              }
              chequesSemFundos={data?.chequesSemFundo}
            />
          </>
        ) : (
          <NoOcurrence />
        )}
      </div>
      <div>
        <TitleStripe className="mb-5">Cheques sustados</TitleStripe>
        {data?.chequesSustados?.length > 0 ? (
          <>
            <ChequesSustados
              quantidadeTotal={
                data?.resumoConsulta?.chequesSustados?.quantidadeTotal
              }
              chequesSustados={data?.chequesSustados}
            />
          </>
        ) : (
          <NoOcurrence />
        )}
      </div>

      <div>
        <TitleStripe className="mb-5">Histórico de consultas</TitleStripe>

        {data?.historicoConsultas?.length > 0 ? (
          <div className="flex flex-col gap-2">
            <div className="flex-1">
              <HistoricoConsultas
                quantidadeTotal={data?.historicoConsultas?.length}
                historicoConsultas={data?.historicoConsultas}
              />
            </div>
            {/* {data?.historicoConsultasResumo?.paresMesAnoHistoricoConsultas
              ?.length > 0 && (
              <ReactApexChart
                className="border border-gray-200 rounded-md mt-3"
                options={{
                  colors: ["var(--primary)"],
                  chart: {
                    height: 150,
                    type: "bar",
                    toolbar: {
                      show: false, // Remove o menu de ações
                    },
                  },
                  plotOptions: {
                    bar: {
                      borderRadius: 2,
                      dataLabels: {
                        position: "center", // top, center, bottom
                      },
                    },
                  },
                  dataLabels: {
                    enabled: true,
                    formatter: function (val) {
                      return val;
                    },
                    offsetY: 0,
                    style: {
                      fontSize: "12px",
                      colors: ["white"],
                    },
                  },

                  xaxis: {
                    categories:
                      data?.historicoConsultasResumo
                        ?.paresMesAnoHistoricoConsultas,
                    position: "top",
                    axisBorder: {
                      show: false,
                    },
                    axisTicks: {
                      show: false,
                    },
                    crosshairs: {
                      fill: {
                        type: "gradient",
                        gradient: {
                          colorFrom: "#30ce2d",
                          colorTo: "#30ce2d",
                          stops: [0, 100],
                          opacityFrom: 0.4,
                          opacityTo: 0.5,
                        },
                      },
                    },
                    tooltip: {
                      enabled: true,
                    },
                  },
                  yaxis: {
                    axisBorder: {
                      show: false,
                    },
                    axisTicks: {
                      show: false,
                    },
                    labels: {
                      show: false,
                      formatter: function (val) {
                        return val;
                      },
                    },
                  },
                  title: {
                    text: "Últimos 12 meses",
                    floating: true,
                    offsetY: 200,
                    align: "center",
                    style: {
                      color: "#444",
                    },
                  },
                }}
                series={[
                  {
                    name: "Qtd. Consultas no período",
                    data: data?.historicoConsultasResumo
                      ?.totaisHistoricoConsultas,
                  },
                ]}
                type="bar"
                height={220}
              />
            )} */}
          </div>
        ) : (
          <NoOcurrence />
        )}
      </div>
      <Modal show={openMap} onClose={() => setOpenMap(false)} size="5xl">
        <Modal.Header>Localização</Modal.Header>
        <Modal.Body>
          {fullAddress ? (
            <StreetView address={fullAddress} height={460} />
          ) : (
            <div className="p-3 text-sm text-gray-500">
              Endereço indisponível.
            </div>
          )}
        </Modal.Body>
      </Modal>
    </div>
  );
};

export default DefineRiscoQuery;
